import './tailwind.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { WrapRoot } from './src/components/WrapRoot';
import { WrapPage } from './src/components/WrapPage';
import { version } from './package.json';
import { initIframeActivityTracking, sendToParent } from './src/utils/postMessageBridge';

import './i18n';

export const wrapRootElement = ({ element }) => {
    return (
        <WrapRoot>
            {element}
        </WrapRoot>
    );
};

export const wrapPageElement = ({ element, props }) => {
    return (
        <WrapPage {...props}>
            {element}
        </WrapPage>
    );
};

export const replaceHydrateFunction = () => {
    return (element, container) => {
        container.setAttribute('data-version', version);
        const root = ReactDOM.createRoot(container);
        root.render(element);
    };
};

/**
 * Called by Gatsby after every client-side route change (including the first
 * render).  We forward the navigation event to the parent site so it can track
 * which page inside the iframe the user is viewing without needing to access
 * the iframe's DOM or URL directly.
 *
 * Navigation events bypass the "active" gate — the parent always needs to know
 * the current route regardless of focus state.
 *
 * Deduplication: In Gatsby dev mode, HMR can trigger onRouteUpdate multiple
 * times for the same path without an actual navigation occurring. We suppress
 * duplicate consecutive route_change events so the parent doesn't see noise.
 */
let _lastSentPath: string | null = null;

export const onRouteUpdate = ({ location, prevLocation }) => {
    const currentPath = location.pathname + location.search;

    // Skip if the path hasn't actually changed (HMR re-renders in dev)
    if (currentPath === _lastSentPath) return;
    _lastSentPath = currentPath;

    sendToParent('navigation', {
        event: 'route_change',
        path: location.pathname,
        search: location.search,
        prevPath: prevLocation?.pathname ?? null,
    });
};

/**
 * Called once when the Gatsby runtime initialises on the client.
 * Registers focus/blur/pointer listeners that tell the bridge whether the
 * iframe is actively being used, so analytics events are only forwarded
 * to the parent when appropriate.
 */
export const onClientEntry = () => {
    initIframeActivityTracking();
};
